import { getCheckoutForSite, activeSiteCountry } from "@config/site/site-config";
import { useStore } from "@store/index";
import { useGetActiveCart } from "@lib/commerce/hooks/cartOperations";
import {
    useAddShippingMethodToCart,
    useUpdateBillingAddress,
    useUpdateDeliveryAddress,
    useUpdateDeliveryDateAndTimeSlot,
} from "@lib/commerce/hooks/checkoutOperations";
import { initializeFormValues as initializeFormValuesFn } from "../checkoutHelper";
import { countryCodeLookup } from "../countryUtility";
import type { Address, Cart } from "@graphql/generated/types";
import { executeCommerceToolsWithRetry } from "../executeCommerceToolsWithRetry";
import { dateSelectorEnabledDeliveryMethods } from "@lib/constants/dateSelectorDeliveryMethods";
import { useLocale } from "next-intl";
import { GraphQLError } from "graphql";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { GraphQLResponse } from "graphql-request";

export type CheckoutFns = {
    countryCode?: string;
    country?: string;
    locale?: string;
};

type CheckoutActionParams = {
    getCart: () => Cart;
    locale: string;
    fns: any;
};

type CheckActionFunction<FuncArgs = { [key: string]: unknown }> = (
    values: FuncArgs
) => Promise<GraphQLResponse<any>>;

type BillingAddressValues = {
    country: string;
    [key: string]: unknown;
};

const addContactDetails = ({ getCart, locale, fns }: CheckoutActionParams): CheckActionFunction => {
    const { executeUpdateDeliveryAddress } = fns;
    return async (values) => {
        const cart = getCart();
        const res = await executeCommerceToolsWithRetry(
            executeUpdateDeliveryAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.shippingAddress,
                ...values,
            },
            0,
            "Adding Contact Details"
        );
        if (!res) {
            Logger.error(ServiceType.COMMERCE_TOOLS, "No response from adding contact details", {
                tag: LogTag.CHECKOUT,
                cartId: cart.id,
                locale,
                values: anonymizeFields(values),
                response: res,
            });
            return {
                errors: [new GraphQLError("No response from adding contact details")],
                data: null,
                status: 500,
            };
        }

        if (res.errors) {
            return res;
        }
        return { ...res, errors: null };
    };
};

const addDeliveryMethod = ({
    getCart,
    locale,
    fns,
}: CheckoutActionParams): CheckActionFunction<string> => {
    const { executeAddShippingMethodToCart } = fns;
    return async (deliveryMethodId: string) => {
        const cart = getCart();
        const res = await executeCommerceToolsWithRetry(
            executeAddShippingMethodToCart,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                // At the moment we have only two delivery methods, so if there is CC, will be click and collect
                shippingMethod: deliveryMethodId,
            },
            0,
            "Adding Delivery Method"
        );
        if (!res) {
            Logger.error(ServiceType.COMMERCE_TOOLS, "No response from adding delivery method", {
                tag: LogTag.CHECKOUT,
                cartId: cart.id,
                locale,
                shippingMethod: deliveryMethodId,
                response: res,
            });
            return {
                errors: [new GraphQLError("No response from adding delivery method")],
                data: null,
                status: 500,
            };
        }
        if (res.errors) {
            return res;
        }
        return { ...res, errors: null };
    };
};

const addDeliveryAddress = ({
    getCart,
    locale,
    fns,
}: CheckoutActionParams): CheckActionFunction => {
    const { executeUpdateDeliveryAddress } = fns;
    return async (values) => {
        const payloadValues = {
            ...values,
        };
        const cart = getCart();
        if (values.country) {
            payloadValues.country = await countryCodeLookup(values.country as string);
        }
        const res = await executeCommerceToolsWithRetry(
            executeUpdateDeliveryAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.shippingAddress,
                ...payloadValues,
            },
            0,
            "Adding Delivery Address"
        );

        if (!res) {
            Logger.error(ServiceType.COMMERCE_TOOLS, "No response from adding delivery address", {
                tag: LogTag.CHECKOUT,
                cartId: cart.id,
                locale,
                values: anonymizeFields(payloadValues),
                response: res,
            });
            return {
                errors: [new GraphQLError("No response from adding delivery address")],
                data: null,
                status: 500,
            };
        }

        if (res.errors) {
            return res;
        }

        return { ...res, errors: null };
    };
};

const addDeliveryDateAndTimeSlot = ({
    getCart,
    locale,
    fns,
}: CheckoutActionParams): CheckActionFunction => {
    const { executeUpdateDeliveryDateAndTimeSlot } = fns;
    return async (values) => {
        const cart = getCart();
        const isCustomFieldAlreadyExistsInCart = (cart: Cart, fieldName: string) => {
            return cart?.custom?.customFieldsRaw?.find((item) => item.name === fieldName)?.value;
        };

        const isDeliveryDateAlreadyExists = isCustomFieldAlreadyExistsInCart(
            cart,
            "order_CF_DeliveryDate"
        );
        const isDeliveryTimeAlreadyExists = isCustomFieldAlreadyExistsInCart(
            cart,
            "order_CF_DeliveryTimeSlot"
        );
        if (
            (!isDeliveryDateAlreadyExists && !values.date) ||
            (!isDeliveryTimeAlreadyExists && !values.timeslot)
        ) {
            return {
                data: null,
                errors: null,
                status: 400,
            };
        }

        const isActiveCountryWithDateSelector =
            activeSiteCountry in dateSelectorEnabledDeliveryMethods;

        if (!values.date && !values.timeslot && !isActiveCountryWithDateSelector)
            return {
                data: null,
                errors: null,
                status: 400,
            };

        const res = await executeCommerceToolsWithRetry(
            executeUpdateDeliveryDateAndTimeSlot,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                date: JSON.stringify(values.date),
                timeslot: JSON.stringify(values.timeslot),
            },
            0,
            "Adding Delivery Date and Time Slot"
        );

        if (!res) {
            Logger.error(
                ServiceType.COMMERCE_TOOLS,
                "No response from adding delivery date and timeslot",
                {
                    tag: LogTag.CHECKOUT,
                    cartId: cart.id,
                    locale,
                    values: anonymizeFields(values),
                    response: res,
                }
            );
            return {
                errors: [new GraphQLError("No response from adding delivery date and timeslot")],
                data: null,
                status: 500,
            };
        }

        if (res.errors) {
            return res;
        }

        return { ...res, errors: null };
    };
};

const addBillingAddress = ({
    getCart,
    locale,
    fns,
}: CheckoutActionParams): CheckActionFunction<BillingAddressValues> => {
    const { executeUpdateBillingAddress } = fns;
    return async (values) => {
        const cart = getCart();
        const oldCountry = values.country;
        const payloadValues = {
            ...values,
        };
        if (oldCountry) {
            payloadValues.country = await countryCodeLookup(oldCountry);
        }

        if (!payloadValues.country) {
            return {
                data: null,
                status: 400,
                errors: [new GraphQLError(`Failed to save billing address by name ${oldCountry}`)],
            };
        }
        const res = await executeCommerceToolsWithRetry(
            executeUpdateBillingAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.billingAddress,
                ...payloadValues,
            },
            0,
            "Adding Billing Address"
        );

        if (!res) {
            Logger.error(ServiceType.COMMERCE_TOOLS, "No response from adding billing address", {
                tag: LogTag.CHECKOUT,
                cartId: cart.id,
                locale,
                values: anonymizeFields(payloadValues),
                response: res,
            });
            return {
                errors: [new GraphQLError("No response from adding billing address")],
                data: null,
                status: 500,
            };
        }

        if (res.errors) {
            return res;
        }

        return { ...res, errors: null };
    };
};

const initializeFormValuesHook = ({ getCart, locale }) => {
    return (cart = getCart()) => {
        const country = activeSiteCountry;
        const { countryCode } = getCheckoutForSite;

        const variables: CheckoutFns = {
            country,
            countryCode,
            locale,
        };

        return initializeFormValuesFn(cart, variables);
    };
};

export const useCheckout = () => {
    const locale = useLocale();
    const executeUpdateDeliveryAddress = useUpdateDeliveryAddress();
    const executeUpdateDeliveryDateAndTimeSlot = useUpdateDeliveryDateAndTimeSlot();
    const executeAddShippingMethodToCart = useAddShippingMethodToCart();
    const executeUpdateBillingAddress = useUpdateBillingAddress();
    const getCartAsync = useGetActiveCart();
    const cart = useStore((state) => state.data.me.cartInfo.cart);
    const getCart = useStore((state) => state.getCart);
    const masterProducts = useStore((state) => state.data.me.cartInfo.masterProducts);

    return {
        addContactDetails: addContactDetails({
            getCart,
            locale,
            fns: { executeUpdateDeliveryAddress },
        }),
        addDeliveryAddress: addDeliveryAddress({
            getCart,
            locale,
            fns: { executeUpdateDeliveryAddress },
        }),
        addDeliveryDateAndTimeSlot: addDeliveryDateAndTimeSlot({
            getCart,
            locale,
            fns: { executeUpdateDeliveryDateAndTimeSlot },
        }),
        addBillingAddress: addBillingAddress({
            getCart,
            locale,
            fns: { executeUpdateBillingAddress },
        }),
        addDeliveryMethod: addDeliveryMethod({
            getCart,
            locale,
            fns: { executeAddShippingMethodToCart },
        }),
        initializeFormValues: initializeFormValuesHook({ getCart, locale }),
        cart,
        getCartAsync,
        masterProducts,
    };
};

export const anonymizeField = (value?: unknown | null) => {
    if (typeof value === "string") {
        // Replace all word characters and numbers (including Unicode/accented characters) with "*".
        return value?.replace(/[\p{L}\p{N}\w]/gu, "*");
    }
    // allow boolean and null and undefined to pass through as itself
    if (value === null || ["undefined", "boolean"].includes(typeof value)) {
        return value;
    }

    if (typeof value === "number") {
        return anonymizeField(value.toString());
    }

    // if value is an object recursively anonymize its fields
    if (typeof value === "object") {
        return anonymizeFields(value as Record<string, unknown>);
    }

    return "REDACTED";
};

export const anonymizeFields = (obj: Record<string, unknown>) => {
    const anonymizedObj: Record<string, unknown> = {};
    Object.entries(obj).forEach(([key, value]) => {
        anonymizedObj[key] = anonymizeField(value);
    });
    return anonymizedObj;
};

type AnonymizeCartAddressError = {
    message: string;
    error?: Record<string, unknown> | unknown;
};
export const anonymizeCartAddress = (address: Address): Address | AnonymizeCartAddressError => {
    if (!address) {
        return {
            message: "No address provided",
        };
    }
    const safeFields: (keyof Address)[] = ["country", "state", "city"];
    try {
        const clonedAddress = JSON.parse(JSON.stringify(address)) as Address;
        return Object.fromEntries(
            Object.entries(clonedAddress).map(([key, val]) => [
                key,
                safeFields.includes(key as keyof Address) ? val : anonymizeField(val),
            ])
        ) as Address;
    } catch (error) {
        return {
            message: "Failed to anonymize cart address",
            error,
        };
    }
};

export const anonymizeCartContactDetails = (address: Address) => {
    if (!address) {
        return {
            message: "No address provided",
        };
    }
    const email = address?.email;
    const phone = address?.phone;
    return anonymizeFields({
        email,
        phone,
    });
};
